import {
  CANDY_MACHINE_CANDIES_RESOURCE_ADDRESS,
  CANDY_WRAPPER_COMPONENT_ADDRESS,
  CANDY_WRAPPER_PAIRS as RAW_CANDY_WRAPPER_PAIRS,
} from "../../constants";
import { CANDY_ORDER, CANDY_VERSIONS, VERSION_ORDER } from "./candyConfig";

const VERSION_INDEX = VERSION_ORDER.reduce((lookup, version, index) => {
  lookup[version] = index;
  return lookup;
}, {});

const CANDY_ORDER_INDEX = CANDY_ORDER.reduce((lookup, candyId, index) => {
  lookup[candyId] = index;
  return lookup;
}, {});

const FAMILY_THEMES = {
  CocoaPop: {
    accent: "#ff9a5f",
    glow: "rgba(255, 154, 95, 0.32)",
    beforeCopy: "Loose cocoa trouble.",
    afterCopy: "Wrapped up and shelf-fit.",
  },
  SlapTaffy: {
    accent: "#ff5e8a",
    glow: "rgba(255, 94, 138, 0.28)",
    beforeCopy: "Sticky little menace.",
    afterCopy: "Twisted tight. Cleaner flex.",
  },
  NutPop: {
    accent: "#e6b457",
    glow: "rgba(230, 180, 87, 0.28)",
    beforeCopy: "Crunchy pocket chaos.",
    afterCopy: "Bagged up like it has manners.",
  },
  ShaftBerry: {
    accent: "#ff4f6f",
    glow: "rgba(255, 79, 111, 0.28)",
    beforeCopy: "Raw berry trouble.",
    afterCopy: "Ribboned up. Trouble with polish.",
  },
};

const DEFAULT_THEME = {
  accent: "#f5b041",
  glow: "rgba(245, 176, 65, 0.28)",
  beforeCopy: "Loose candy being unruly.",
  afterCopy: "Wrapped candy with better manners.",
};

const getFamilyTheme = (family) => FAMILY_THEMES[family] || DEFAULT_THEME;

const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const buildShortDescription = (family, version) =>
  `${family} ${version}. Same candy, less goblin energy.`;

const buildDisplayName = (family, version) => `${family} ${version}`;

const buildWrappedLabel = (family, version) =>
  `${family} ${version} Gift Wrap`;

const sortPairs = (left, right) => {
  const leftFamilyIndex = CANDY_ORDER_INDEX[slugify(left.family)];
  const rightFamilyIndex = CANDY_ORDER_INDEX[slugify(right.family)];

  if (leftFamilyIndex !== rightFamilyIndex) {
    if (leftFamilyIndex !== undefined && rightFamilyIndex !== undefined) {
      return leftFamilyIndex - rightFamilyIndex;
    }

    if (leftFamilyIndex !== undefined) {
      return -1;
    }

    if (rightFamilyIndex !== undefined) {
      return 1;
    }

    return left.family.localeCompare(right.family);
  }

  return (VERSION_INDEX[left.version] ?? 999) - (VERSION_INDEX[right.version] ?? 999);
};

const normalizePairOverride = (pairConfig) => {
  if (typeof pairConfig === "string") {
    return { wrappedResourceAddress: pairConfig };
  }

  if (pairConfig && typeof pairConfig === "object") {
    return pairConfig;
  }

  return {};
};

const getMergedPairConfig = (unwrappedResourceAddress, overrideConfig = {}) => ({
  ...normalizePairOverride(RAW_CANDY_WRAPPER_PAIRS[unwrappedResourceAddress]),
  ...normalizePairOverride(overrideConfig),
});

const buildPairModel = (unwrappedResourceAddress, overrideConfig = {}) => {
  const onLedgerMetadata =
    CANDY_MACHINE_CANDIES_RESOURCE_ADDRESS[unwrappedResourceAddress] || {};
  const family = overrideConfig.family || onLedgerMetadata.name || "Candy";
  const version = overrideConfig.version || onLedgerMetadata.rarity || "Street";
  const versionConfig = CANDY_VERSIONS[version] || CANDY_VERSIONS.Street;
  const theme = getFamilyTheme(family);
  const wrappedResourceAddress = overrideConfig.wrappedResourceAddress || "";

  return {
    unwrappedResourceAddress,
    wrappedResourceAddress,
    slug:
      overrideConfig.slug ||
      `${slugify(family)}-${slugify(version)}-wrapper`,
    family,
    version,
    displayName: buildDisplayName(family, version),
    shortDescription: buildShortDescription(family, version),
    beforeLabel: `${family} ${version} Loose`,
    afterLabel: buildWrappedLabel(family, version),
    beforeCopy: theme.beforeCopy,
    afterCopy: theme.afterCopy,
    accent: theme.accent,
    glow: theme.glow,
    versionColor: versionConfig.color,
    versionGradient: versionConfig.gradient,
    isConfigured:
      Boolean(CANDY_WRAPPER_COMPONENT_ADDRESS) &&
      Boolean(wrappedResourceAddress),
    isLivePair: Boolean(wrappedResourceAddress),
  };
};

export const CANDY_WRAPPER_PAIRS = Object.entries(RAW_CANDY_WRAPPER_PAIRS || {})
  .map(([unwrappedResourceAddress, pairConfig]) =>
    buildPairModel(unwrappedResourceAddress, normalizePairOverride(pairConfig))
  )
  .sort(sortPairs);

export const HAS_WRAPPER_COMPONENT_ADDRESS = Boolean(
  CANDY_WRAPPER_COMPONENT_ADDRESS
);

export const getCandyWrapperPair = (unwrappedResourceAddress) =>
  buildPairModel(
    unwrappedResourceAddress,
    getMergedPairConfig(unwrappedResourceAddress)
  );
